import { useState, useMemo } from 'react';
import { theme } from '../../styles/theme';
import { Card } from '../../types/game';
import { CARD_TEMPLATES, createCard } from '../../data/cards';

interface DraftStep {
  label: string;
  description: string;
  fixedKeys?: string[];
  pool?: string[];
}

const DRAFT_STEPS: DraftStep[] = [
  {
    label: 'MOVEMENT',
    description: 'Choose a movement card to complement your 5 base Move cards.',
    fixedKeys: ['shortcut', 'quickStep', 'sprint'],
  },
  {
    label: 'INTEL I',
    description: 'Choose an intel card. Intel lets you locate the agent.',
    pool: Object.keys(CARD_TEMPLATES).filter(k => CARD_TEMPLATES[k].category === 'intel'),
  },
  {
    label: 'INTEL II',
    description: 'Choose a second intel card.',
    pool: Object.keys(CARD_TEMPLATES).filter(k => CARD_TEMPLATES[k].category === 'intel'),
  },
  {
    label: 'TACTICS I',
    description: 'Choose a tactics card for evasion and deception.',
    pool: Object.keys(CARD_TEMPLATES).filter(k => CARD_TEMPLATES[k].category === 'tactics'),
  },
  {
    label: 'TACTICS II',
    description: 'Choose a second tactics card.',
    pool: Object.keys(CARD_TEMPLATES).filter(k => CARD_TEMPLATES[k].category === 'tactics'),
  },
  {
    label: 'FIELDCRAFT',
    description: 'Choose a fieldcraft card to enhance your other cards.',
    pool: Object.keys(CARD_TEMPLATES).filter(k => CARD_TEMPLATES[k].category === 'fieldcraft'),
  },
];

function pickRandom(pool: string[], count: number, exclude: Set<string>): string[] {
  const avail = pool.filter(k => !exclude.has(k));
  return [...avail].sort(() => Math.random() - 0.5).slice(0, count);
}

const CAT_COLORS: Record<string, string> = {
  movement: '#66ccff',
  intel: '#aa88ff',
  tactics: '#ffaa44',
  combat: '#ff5555',
  fieldcraft: '#44cc88',
};

interface DeckDraftScreenProps {
  onComplete: (draftedCards: Card[]) => void;
  isLoading?: boolean;
  loadingStage?: string;
  loadingProgress?: number;
}

// Base cards always in the starter deck
const BASE_CARDS: Array<{ name: string; category: string; apCost: number }> = [
  { name: 'Move', category: 'movement', apCost: 1 },
  { name: 'Move', category: 'movement', apCost: 1 },
  { name: 'Move', category: 'movement', apCost: 1 },
  { name: 'Move', category: 'movement', apCost: 1 },
  { name: 'Move', category: 'movement', apCost: 1 },
  { name: 'Imp. Strike', category: 'combat', apCost: 1 },
];

export function DeckDraftScreen({ onComplete, isLoading, loadingStage, loadingProgress }: DeckDraftScreenProps) {
  const [stepIndex, setStepIndex] = useState(0);
  const [pickedCards, setPickedCards] = useState<Card[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pickedKeys, setPickedKeys] = useState<Set<string>>(new Set());
  // Animation: track the most recently added card id for the fly-in effect
  const [lastAddedId, setLastAddedId] = useState<string | null>(null);
  const [launching, setLaunching] = useState(false);

  const done = stepIndex >= DRAFT_STEPS.length;
  const step = done ? null : DRAFT_STEPS[stepIndex];

  const options = useMemo(() => {
    if (!step) return [];
    if (step.fixedKeys) return step.fixedKeys.map(k => ({ key: k, card: createCard(k) }));
    if (step.pool) return pickRandom(step.pool, 3, pickedKeys).map(k => ({ key: k, card: createCard(k) }));
    return [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex]);

  const handlePick = () => {
    if (!selectedId) return;
    const picked = options.find(o => o.card.id === selectedId);
    if (!picked) return;
    setLastAddedId(picked.card.id);
    setPickedCards(prev => [...prev, picked.card]);
    setPickedKeys(prev => { const n = new Set(prev); n.add(picked.key); return n; });
    setSelectedId(null);
    setStepIndex(prev => prev + 1);
    // Clear animation flag after animation completes
    setTimeout(() => setLastAddedId(null), 500);
  };

  const handleFinish = () => {
    const baseDeck: Card[] = [
      createCard('move'), createCard('move'), createCard('move'), createCard('move'), createCard('move'),
      createCard('improvisedStrike'),
      ...pickedCards,
    ];
    onComplete(baseDeck);
  };

  // Mini card renderer (for both options and deck bar)
  const renderCard = (card: { name: string; category: string; apCost: number; description?: string; id?: string }, opts: {
    selected?: boolean;
    mini?: boolean;
    onClick?: () => void;
    animateIn?: boolean;
    dimmed?: boolean;
  } = {}) => {
    const { selected, mini, onClick, animateIn, dimmed } = opts;
    const catColor = CAT_COLORS[card.category] ?? '#888';
    const w = mini ? 70 : 100;
    const h = mini ? 95 : 135;
    return (
      <div
        key={card.id ?? card.name}
        className={onClick && !mini ? 'draft-card-option' : undefined}
        onClick={onClick}
        style={{
          width: `${w}px`,
          height: `${h}px`,
          background: selected ? `${catColor}20` : 'rgba(12, 16, 24, 0.95)',
          border: `1.5px solid ${selected ? catColor : dimmed ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.12)'}`,
          borderRadius: '6px',
          padding: mini ? '4px 5px' : '8px 8px',
          display: 'flex',
          flexDirection: 'column' as const,
          alignItems: 'center',
          justifyContent: 'center',
          gap: mini ? '2px' : '4px',
          cursor: onClick ? 'pointer' : 'default',
          transition: 'all 0.15s ease',
          transform: selected ? 'translateY(-6px)' : 'none',
          boxShadow: selected ? `0 4px 16px ${catColor}30` : 'none',
          opacity: dimmed ? 0.5 : 1,
          position: 'relative' as const,
          flexShrink: 0,
          ...(animateIn ? {
            animation: 'draft-card-enter 0.4s cubic-bezier(0.2, 0.7, 0.3, 1) backwards',
          } : {}),
        }}
      >
        {/* AP cost badge */}
        <div style={{
          position: 'absolute',
          top: mini ? '2px' : '4px',
          left: mini ? '3px' : '5px',
          width: mini ? '16px' : '22px',
          height: mini ? '16px' : '22px',
          borderRadius: '4px',
          background: catColor,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: mini ? '9px' : '12px',
          fontWeight: 800,
          color: '#000',
          fontFamily: theme.fonts.mono,
        }}>
          {card.apCost}
        </div>
        {/* Card name */}
        <div style={{
          fontFamily: theme.fonts.mono,
          fontSize: mini ? '8px' : '11px',
          fontWeight: 700,
          color: selected ? catColor : theme.colors.textPrimary,
          textAlign: 'center',
          lineHeight: '1.2',
          marginTop: mini ? '12px' : '18px',
        }}>
          {card.name}
        </div>
        {/* Category */}
        <div style={{
          fontFamily: theme.fonts.mono,
          fontSize: mini ? '6px' : '8px',
          color: catColor,
          letterSpacing: '1px',
          textTransform: 'uppercase',
        }}>
          {card.category}
        </div>
        {/* Hover tooltip with full details (only on non-mini selectable cards) */}
        {!mini && card.description && (
          <div className="draft-card-tooltip" style={{
            position: 'absolute',
            bottom: 'calc(100% + 10px)',
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'rgba(8, 12, 20, 0.98)',
            border: `1px solid ${catColor}60`,
            borderRadius: '6px',
            padding: '10px 12px',
            minWidth: '220px',
            maxWidth: '280px',
            display: 'none',
            zIndex: 100,
            pointerEvents: 'none',
          }}>
            <div style={{
              fontFamily: theme.fonts.mono, fontSize: '11px', fontWeight: 700,
              color: catColor, letterSpacing: '1px', marginBottom: '2px',
            }}>{card.name}</div>
            <div style={{
              fontFamily: theme.fonts.mono, fontSize: '8px', color: catColor,
              letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '6px',
            }}>{card.category} · {card.apCost} AP</div>
            <div style={{
              fontFamily: theme.fonts.mono, fontSize: '10px',
              color: theme.colors.textPrimary, lineHeight: '1.5', whiteSpace: 'normal',
            }}>{card.description}</div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: theme.colors.background,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '30px 20px 140px',
    }}>
      {/* Inline keyframes + hover styles */}
      <style>{`
        @keyframes draft-card-enter {
          from { transform: translateY(-60px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .draft-card-option:hover .draft-card-tooltip {
          display: block !important;
        }
      `}</style>

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '8px' }}>
        <div style={{
          fontFamily: theme.fonts.mono, fontSize: '10px', color: '#ff4444',
          letterSpacing: '4px', marginBottom: '10px',
        }}>TOP SECRET // NOFORN</div>
        <h1 style={{
          fontFamily: theme.fonts.mono, fontSize: '28px', fontWeight: 900,
          color: theme.colors.textPrimary, letterSpacing: '6px', margin: 0,
        }}>MISSION PREP</h1>
        <div style={{
          fontFamily: theme.fonts.mono, fontSize: '11px', color: theme.colors.textSecondary,
          letterSpacing: '3px', marginTop: '6px',
        }}>SELECT YOUR STARTING DECK — 12 CARDS</div>
      </div>

      {/* Progress dots */}
      <div style={{ display: 'flex', gap: '8px', margin: '16px 0 20px' }}>
        {DRAFT_STEPS.map((_, i) => (
          <div key={i} style={{
            width: '10px', height: '10px', borderRadius: '50%',
            background: i < stepIndex ? '#44cc88' : i === stepIndex ? '#44aaff' : 'rgba(255,255,255,0.15)',
            transition: 'background 0.3s ease',
          }} />
        ))}
      </div>

      {!done && step ? (
        <>
          {/* Step label */}
          <div style={{ textAlign: 'center', marginBottom: '16px' }}>
            <div style={{
              fontFamily: theme.fonts.mono, fontSize: '13px', fontWeight: 700,
              color: '#44aaff', letterSpacing: '3px', marginBottom: '4px',
            }}>{stepIndex + 1}/6 — {step.label}</div>
            <div style={{
              fontFamily: theme.fonts.mono, fontSize: '10px',
              color: theme.colors.textSecondary, lineHeight: '1.4',
            }}>{step.description}</div>
          </div>

          {/* Card options — displayed as card shapes */}
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            {options.map(({ card }) =>
              renderCard(card, {
                selected: selectedId === card.id,
                onClick: () => setSelectedId(card.id),
              })
            )}
          </div>

          {/* Confirm */}
          <button
            disabled={!selectedId}
            style={{
              marginTop: '20px', padding: '10px 32px',
              background: selectedId ? 'rgba(68, 170, 255, 0.15)' : 'rgba(60,60,60,0.2)',
              border: `1px solid ${selectedId ? '#44aaff' : 'rgba(100,100,100,0.3)'}`,
              borderRadius: '6px', color: selectedId ? '#44aaff' : '#555',
              fontFamily: theme.fonts.mono, fontSize: '12px', fontWeight: 700,
              letterSpacing: '2px', cursor: selectedId ? 'pointer' : 'not-allowed',
            }}
            onClick={handlePick}
          >CONFIRM</button>
        </>
      ) : (
        /* Finished drafting */
        <div style={{ textAlign: 'center' }}>
          <div style={{
            fontFamily: theme.fonts.mono, fontSize: '14px', fontWeight: 700,
            color: '#44cc88', letterSpacing: '3px', marginBottom: '20px',
          }}>DECK READY</div>
          <button
            disabled={isLoading || launching}
            style={{
              padding: '12px 40px',
              background: (isLoading || launching) ? 'rgba(60,60,60,0.2)' : 'rgba(68, 204, 136, 0.15)',
              border: `1px solid ${(isLoading || launching) ? 'rgba(100,100,100,0.3)' : '#44cc88'}`,
              borderRadius: '6px', color: (isLoading || launching) ? '#888' : '#44cc88',
              fontFamily: theme.fonts.mono, fontSize: '14px', fontWeight: 700,
              letterSpacing: '3px', cursor: (isLoading || launching) ? 'wait' : 'pointer',
            }}
            onClick={() => { setLaunching(true); handleFinish(); }}
          >{launching ? 'LAUNCHING...' : isLoading ? 'LOADING MAP...' : 'BEGIN MISSION'}</button>
          {isLoading && loadingStage && (
            <div style={{
              marginTop: '12px', fontFamily: theme.fonts.mono, fontSize: '9px',
              color: theme.colors.textSecondary, letterSpacing: '1px',
            }}>
              {loadingStage}
              {loadingProgress != null && (
                <div style={{
                  width: '200px', height: '3px', background: 'rgba(255,255,255,0.06)',
                  borderRadius: '2px', margin: '6px auto 0', overflow: 'hidden',
                }}>
                  <div style={{
                    width: `${Math.round((loadingProgress ?? 0) * 100)}%`, height: '100%',
                    background: '#44cc88', borderRadius: '2px', transition: 'width 0.3s ease',
                  }} />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Bottom deck bar — always visible */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'rgba(8, 12, 20, 0.95)',
        borderTop: '1px solid rgba(255,255,255,0.08)',
        padding: '10px 16px 14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '6px',
        overflowX: 'auto',
      }}>
        <div style={{
          fontFamily: theme.fonts.mono, fontSize: '8px', color: '#555',
          letterSpacing: '1px', marginRight: '8px', flexShrink: 0,
          writingMode: 'vertical-lr', transform: 'rotate(180deg)',
        }}>DECK ({6 + pickedCards.length}/12)</div>
        {/* Base cards (dimmed) */}
        {BASE_CARDS.map((c, i) =>
          renderCard(c, { mini: true, dimmed: true, key: `base-${i}` } as any)
        )}
        {/* Drafted cards with enter animation on the newest */}
        {pickedCards.map(card =>
          renderCard(card, { mini: true, animateIn: card.id === lastAddedId })
        )}
      </div>
    </div>
  );
}
